import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ChevronLeft, FileText, PlayCircle, Link as LinkIcon, StickyNote, ListChecks,
  CheckCircle2, ExternalLink, Pencil, Users, ShoppingBag, Megaphone, Truck,
  ShoppingCart, Briefcase, MapPin, Folder,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = { Users, ShoppingBag, Megaphone, Truck, ShoppingCart, Briefcase, MapPin, Folder };

function getEmbedUrl(url) {
  if (!url) return null;
  let m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  return null;
}

function ChecklistResource({ resource, accent, onUpdate }) {
  const [completedIds, setCompletedIds] = useState(new Set(resource.completed_item_ids || []));
  const [busy, setBusy] = useState(null);

  const items = (resource.checklist_items || []).slice().sort((a, b) => a.order - b.order);
  const total = items.length;
  const done = completedIds.size;
  const pct = total ? Math.round((done / total) * 100) : 0;

  const toggle = async (itemId) => {
    setBusy(itemId);
    try {
      const r = await api.post(`/checklists/${resource.id}/items/${itemId}/toggle`);
      const next = new Set(completedIds);
      if (r.data.completed) next.add(itemId); else next.delete(itemId);
      setCompletedIds(next);
      if (r.data.points_awarded) {
        toast.success(`+${r.data.points_awarded.points} pts · ${r.data.points_awarded.reason}`, { duration: 4500 });
      }
      onUpdate?.();
    } catch {
      toast.error("Error al actualizar");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden"
      data-testid={`pres-${resource.id}`}
      style={{ borderLeft: `3px solid ${accent}` }}
    >
      <div className="px-5 py-4 border-b border-[#E5E7EB]">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <ListChecks className="h-4 w-4" style={{ color: accent }} strokeWidth={1.5} />
            <h3 className="font-heading text-lg text-[#1A1A1A]">{resource.title}</h3>
          </div>
          <span className="text-xs text-[#737373]">{done} / {total}</span>
        </div>
        {resource.description && (
          <p className="text-sm text-[#374151] mb-3">{resource.description}</p>
        )}
        <div className="h-1.5 rounded-full bg-[#E5E7EB] overflow-hidden">
          <div
            className="h-full transition-all duration-500"
            style={{ width: `${pct}%`, background: accent }}
          />
        </div>
      </div>
      <ul className="divide-y divide-[#E5E7EB]">
        {items.map((it, i) => {
          const checked = completedIds.has(it.id);
          return (
            <li key={it.id} className="px-5 py-3">
              <button
                onClick={() => !busy && toggle(it.id)}
                data-testid={`checklist-item-${it.id}`}
                disabled={busy === it.id}
                className="w-full flex items-start gap-3 text-left group"
              >
                <span
                  className={`h-5 w-5 rounded-md flex items-center justify-center shrink-0 mt-0.5 border-2 transition-colors ${
                    checked
                      ? "border-transparent text-white"
                      : "border-[#E5E7EB] group-hover:border-[#6B7280]"
                  }`}
                  style={{ background: checked ? accent : "transparent" }}
                >
                  {checked && <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.5} />}
                </span>
                <div className="flex-1">
                  <p
                    className={`text-sm leading-relaxed ${
                      checked ? "text-[#737373] line-through" : "text-[#1A1A1A]"
                    }`}
                  >
                    <span className="text-[#6B7280] mr-1.5">{i + 1}.</span>
                    {it.text}
                  </p>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ResourceCard({ resource, accent, onUpdate }) {
  if (resource.resource_type === "checklist") {
    return <ChecklistResource resource={resource} accent={accent} onUpdate={onUpdate} />;
  }

  if (resource.resource_type === "note") {
    return (
      <div
        className="bg-white border border-[#E5E7EB] rounded-md p-5"
        data-testid={`pres-${resource.id}`}
        style={{ borderLeft: `3px solid ${accent}` }}
      >
        <div className="flex items-center gap-2 mb-2">
          <StickyNote className="h-4 w-4" style={{ color: accent }} strokeWidth={1.5} />
          <h4 className="font-heading text-base text-[#1A1A1A]">{resource.title}</h4>
        </div>
        {resource.description && <p className="text-xs text-[#737373] mb-2">{resource.description}</p>}
        <p className="text-sm text-[#1A1A1A] whitespace-pre-wrap leading-relaxed">{resource.body}</p>
      </div>
    );
  }

  const fileSrc = resource.url_type === "url" ? resource.url : `${API}/files/${resource.url}`;

  if (resource.resource_type === "video") {
    const embed = getEmbedUrl(resource.url);
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden" data-testid={`pres-${resource.id}`}>
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center gap-2">
          <PlayCircle className="h-4 w-4" style={{ color: accent }} strokeWidth={1.5} />
          <h4 className="text-sm font-medium text-[#1A1A1A]">{resource.title}</h4>
        </div>
        <div className="video-shell">
          {embed ? (
            <iframe src={embed} title={resource.title} allow="autoplay; encrypted-media" allowFullScreen />
          ) : resource.url_type === "upload" ? (
            <video controls src={fileSrc} />
          ) : (
            <div className="flex items-center justify-center h-48 text-white">
              <a href={resource.url} target="_blank" rel="noreferrer" className="underline">Abrir video</a>
            </div>
          )}
        </div>
        {resource.description && (
          <p className="px-4 py-2 text-xs text-[#737373] bg-[#FFFFFF]">{resource.description}</p>
        )}
      </div>
    );
  }

  if (resource.resource_type === "pdf") {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden" data-testid={`pres-${resource.id}`}>
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="h-4 w-4 shrink-0" style={{ color: accent }} strokeWidth={1.5} />
            <h4 className="text-sm font-medium text-[#1A1A1A] truncate">{resource.title}</h4>
          </div>
          <a href={fileSrc} target="_blank" rel="noreferrer" className="text-xs text-[#1B2E7A] hover:underline inline-flex items-center gap-1 shrink-0">
            <ExternalLink className="h-3.5 w-3.5" /> Abrir
          </a>
        </div>
        <iframe src={fileSrc} title={resource.title} className="w-full" style={{ height: "60vh" }} />
        {resource.description && (
          <p className="px-4 py-2 text-xs text-[#737373] bg-[#FFFFFF]">{resource.description}</p>
        )}
      </div>
    );
  }

  // link
  return (
    <a
      href={resource.url}
      target="_blank"
      rel="noreferrer"
      data-testid={`pres-${resource.id}`}
      className="bg-white border border-[#E5E7EB] rounded-md p-4 flex items-start gap-3 hover:shadow-md hover:border-[#E5E7EB] transition-all"
    >
      <div className="h-10 w-10 rounded-md flex items-center justify-center shrink-0" style={{ background: `${accent}15`, color: accent }}>
        <LinkIcon className="h-5 w-5" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-[#1A1A1A] mb-0.5">{resource.title}</h4>
        <p className="text-xs text-[#6B7280] truncate">{resource.url}</p>
        {resource.description && <p className="text-xs text-[#737373] mt-1">{resource.description}</p>}
      </div>
      <ExternalLink className="h-4 w-4 text-[#6B7280] shrink-0 mt-1" />
    </a>
  );
}

export default function ProcessCategoryDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cat, setCat] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/process-categories/${slug}`);
      setCat(r.data);
    } catch {
      setCat(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [slug]);

  if (loading) return <div className="text-[#374151]">Cargando...</div>;
  if (!cat) return <div className="text-[#374151]">Categoría no encontrada.</div>;

  const accent = cat.color || "#1B2E7A";
  const Icon = ICONS[cat.icon] || Folder;

  return (
    <div data-testid="process-category-page">
      <button
        onClick={() => navigate("/procesos")}
        className="inline-flex items-center gap-1 text-sm text-[#374151] hover:text-[#1A1A1A] mb-6"
        data-testid="back-to-procesos"
      >
        <ChevronLeft className="h-4 w-4" /> Procesos
      </button>

      <div
        className="rounded-md overflow-hidden border border-[#E5E7EB] mb-8 fade-up"
        style={{ background: `linear-gradient(120deg, ${accent} 0%, ${accent}D9 55%, #FFFFFF 55%)` }}
      >
        <div className="p-6 md:p-8 flex flex-col md:flex-row gap-6 md:items-center">
          <div className="md:w-2/5 text-white">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/70 mb-2 inline-flex items-center gap-1.5">
              <Icon className="h-3.5 w-3.5" /> Categoría
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight">{cat.name}</h1>
            {cat.tagline && (
              <p className="text-base text-white/85 mt-2 italic">{cat.tagline}</p>
            )}
          </div>
          <div className="flex-1 md:py-2">
            <p className="text-sm text-[#374151] leading-relaxed">{cat.description}</p>
            {user?.role === "admin" && (
              <Link
                to="/admin/procesos"
                className="inline-flex items-center gap-1.5 mt-4 bg-[#1B2E7A] text-white px-3 py-1.5 rounded-md text-xs hover:bg-[#13205A]"
              >
                <Pencil className="h-3.5 w-3.5" /> Editar materiales
              </Link>
            )}
          </div>
        </div>
      </div>

      {(!cat.resources || cat.resources.length === 0) ? (
        <div className="bg-white border border-[#E5E7EB] rounded-md p-8 text-center text-[#737373]" data-testid="no-resources">
          Esta categoría aún no tiene materiales.
          {user?.role === "admin" && (
            <p className="mt-2">
              <Link to="/admin/procesos" className="text-[#1B2E7A] underline">Cárgalos desde el panel admin</Link>
            </p>
          )}
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-5">
          {cat.resources.map((r) => (
            <ResourceCard key={r.id} resource={r} accent={accent} onUpdate={load} />
          ))}
        </div>
      )}
    </div>
  );
}
